import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import {
  Home, FileText, AlertCircle, Gift, Users, Building2,
  ChevronRight, Loader2, Calendar, Clock, TrendingUp,
  Star, Trophy, Bell
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BuyerDashboard() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  
  const buyerData = JSON.parse(localStorage.getItem('buyerData') || '{}');
  const buyerId = buyerData?.id;

  useEffect(() => {
    if (buyerId) {
      loadDashboard();
    }
  }, [buyerId]);

  const loadDashboard = async () => {
    try {
      const token = localStorage.getItem('buyerToken');
      const res = await fetch(`${API}/api/buyer-portal/dashboard?buyer_id=${buyerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getTierProgress = () => {
    const tier = dashboard?.buyer?.loyalty_tier || 'bronze';
    const referrals = dashboard?.buyer?.successful_referrals || 0;
    
    const tiers = {
      bronze: { next: 'Silver', target: 3, current: referrals },
      silver: { next: 'Gold', target: 7, current: referrals },
      gold: { next: 'Platinum', target: 15, current: referrals },
      platinum: { next: null, target: 15, current: referrals }
    };
    
    const tierData = tiers[tier] || tiers.bronze;
    const progress = Math.min((tierData.current / tierData.target) * 100, 100);
    
    return { ...tierData, progress };
  };

  const tierProgress = getTierProgress();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Welcome, {dashboard?.buyer?.name}!</h1>
            <p className="text-amber-100 mt-1">
              Unit {dashboard?.buyer?.unit_number} | {dashboard?.project?.name}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <Badge className="bg-white/20 text-white border-0 text-lg px-3 py-1">
                {dashboard?.buyer?.loyalty_tier?.toUpperCase()}
              </Badge>
              <p className="text-sm text-amber-100 mt-1">{dashboard?.stats?.loyalty_points} points</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.stats?.documents_count || 0}</p>
                <p className="text-xs text-muted-foreground">Documents</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.stats?.open_complaints || 0}</p>
                <p className="text-xs text-muted-foreground">Open Tickets</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Gift className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.stats?.successful_referrals || 0}</p>
                <p className="text-xs text-muted-foreground">Referrals</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Star className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.stats?.loyalty_points || 0}</p>
                <p className="text-xs text-muted-foreground">Points</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Loyalty Progress */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-600" />
              Loyalty Progress
            </CardTitle>
            <CardDescription>Refer more friends to level up</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-medium capitalize">{dashboard?.buyer?.loyalty_tier}</span>
                {tierProgress.next && (
                  <span className="text-sm text-muted-foreground">
                    {tierProgress.current}/{tierProgress.target} to {tierProgress.next}
                  </span>
                )}
              </div>
              <Progress value={tierProgress.progress} className="h-3" />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Bronze</span>
                <span>Silver</span>
                <span>Gold</span>
                <span>Platinum</span>
              </div>
              <Link to="/buyer/referrals">
                <Button variant="outline" className="w-full mt-2">
                  <Gift className="h-4 w-4 mr-2" />
                  Refer a Friend & Earn Points
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Property Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-amber-600" />
              Property Status
            </CardTitle>
            <CardDescription>Your property details</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Unit</span>
                <span className="font-medium">{dashboard?.buyer?.unit_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Project</span>
                <span className="font-medium">{dashboard?.project?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <Badge variant="outline" className="capitalize">
                  {dashboard?.buyer?.status?.replace('_', ' ')}
                </Badge>
              </div>
              {dashboard?.buyer?.is_rented && dashboard?.tenant && (
                <div className="pt-2 border-t">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tenant</span>
                    <span className="font-medium">{dashboard?.tenant?.name}</span>
                  </div>
                  {dashboard?.stats?.pending_rent > 0 && (
                    <div className="flex justify-between mt-2">
                      <span className="text-muted-foreground">Pending Rent</span>
                      <span className="font-medium text-red-600">
                        ₹{dashboard?.stats?.pending_rent?.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Link to="/buyer/documents">
              <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                <FileText className="h-6 w-6 text-blue-600" />
                <span>View Documents</span>
              </Button>
            </Link>
            <Link to="/buyer/complaints">
              <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                <AlertCircle className="h-6 w-6 text-red-600" />
                <span>Raise Complaint</span>
              </Button>
            </Link>
            <Link to="/buyer/referrals">
              <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                <Gift className="h-6 w-6 text-green-600" />
                <span>Refer Friends</span>
              </Button>
            </Link>
            <Link to="/buyer/rent">
              <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                <Users className="h-6 w-6 text-purple-600" />
                <span>Rent Management</span>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Construction Update (if pre-possession) */}
      {dashboard?.latest_construction_update && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-amber-600" />
              Latest Construction Update
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">{dashboard.latest_construction_update.title}</h4>
                <Badge>{dashboard.latest_construction_update.milestone}</Badge>
              </div>
              <Progress value={dashboard.latest_construction_update.progress_percentage} />
              <p className="text-sm text-muted-foreground">
                {dashboard.latest_construction_update.description}
              </p>
              {dashboard.latest_construction_update.images?.length > 0 && (
                <div className="flex gap-2 mt-2">
                  {dashboard.latest_construction_update.images.slice(0, 3).map((img, i) => (
                    <img key={i} src={img} alt="" className="w-20 h-20 object-cover rounded-lg" />
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
